import { Inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  GetTermsOfServiceResponse,
  GetUserAgreementTermsOfServiceResponse,
  PartnerTermsOfApiService,
  PartnerUserAgreementTermsOfApiService,
  TermsOfService,
  UserAgreementTermsOfService,
} from '@galaxy/partner';
import {
  catchError,
  combineLatest,
  defaultIfEmpty,
  EMPTY,
  map,
  Observable,
  of,
  ReplaySubject,
  shareReplay,
  switchMap,
} from 'rxjs';
import { getPlatformLocationToSKey } from './conversation-utils';
import { InboxService } from './inbox.service';
import { PARTNER_ID_TOKEN, USER_ID_TOKEN } from './tokens';
import { filterNullAndUndefined } from '@vendasta/rx-utils';

export interface PlatformLocationToSKey {
  partnerId: string;
  feature: string;
}

@Injectable()
export class InboxTermsOfService {
  readonly termsOfServiceAccepted$$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  public termsOfServiceAccepted$: Observable<boolean> = this.termsOfServiceAccepted$$.asObservable();

  platformLocationToSKey$ = of(getPlatformLocationToSKey(this.inboxService.platformLocation)).pipe(
    filterNullAndUndefined(),
  );

  readonly userAgreementTerms$: Observable<GetUserAgreementTermsOfServiceResponse> = this.platformLocationToSKey$.pipe(
    switchMap((platformLocationToSKey) =>
      this.getUserAgreement({
        feature: platformLocationToSKey.feature,
      }),
    ),
  );

  readonly termsOfService$: Observable<TermsOfService> = this.platformLocationToSKey$.pipe(
    switchMap((platformLocationToSKey) =>
      this.getTermsOfService({
        partnerId: platformLocationToSKey.partnerId,
        feature: platformLocationToSKey.feature,
      }).pipe(map((tos) => tos?.termsOfService)),
    ),
  );

  readonly getTermsOfServiceText$: Observable<string> = combineLatest([
    this.userAgreementTerms$,
    this.termsOfService$,
  ]).pipe(
    map(([userAgreementResponse, tos]) => {
      const needToAcceptTermsOfService = tos
        ? (this.isNewTermsOfService({
            userAgreement: userAgreementResponse?.userAgreementTermsOfService,
            terms: tos,
          }) ||
            !this.userAgreementIsAccepted({
              userAgreement: userAgreementResponse?.userAgreementTermsOfService,
            })) &&
          this.hasTermsOfService({
            terms: tos,
          })
        : false;
      if (needToAcceptTermsOfService) {
        this.termsOfServiceAccepted$$.next(false);
        return tos?.termsOfService;
      } else {
        this.termsOfServiceAccepted$$.next(true);
        return '';
      }
    }),
    shareReplay(1),
  );

  constructor(
    private partnerTermsOfApiService: PartnerTermsOfApiService,
    private partnerUserAgreementTermsOfApiService: PartnerUserAgreementTermsOfApiService,
    private snackBar: MatSnackBar,
    @Inject(PARTNER_ID_TOKEN) private partnerId$: Observable<string>,
    @Inject(USER_ID_TOKEN) private userId$: Observable<string>,
    private inboxService: InboxService,
  ) {}

  getTermsOfService(termsOfService: {
    partnerId?: string;
    marketId?: string;
    feature?: string;
  }): Observable<GetTermsOfServiceResponse> {
    return this.partnerId$.pipe(
      map((partnerId) => ({
        partnerId,
        ...termsOfService,
        marketId: termsOfService?.marketId || 'NULL',
      })),
      switchMap((termsOfService) => this.partnerTermsOfApiService.getTermsOfService(termsOfService)),
    );
  }

  getUserAgreement(userAgreement: {
    partnerId?: string;
    userId?: string;
    feature: string;
  }): Observable<GetUserAgreementTermsOfServiceResponse> {
    return combineLatest([this.partnerId$, this.userId$]).pipe(
      map(([partnerId, userId]) => ({
        partnerId,
        userId,
        ...userAgreement,
      })),
      switchMap((userAgreement) =>
        this.partnerUserAgreementTermsOfApiService.getUserAgreementTermsOfService(userAgreement).pipe(
          catchError((e) => {
            console.warn('Error getting term of service', e);
            return EMPTY;
          }),
          defaultIfEmpty({} as GetUserAgreementTermsOfServiceResponse),
        ),
      ),
    );
  }

  hasTermsOfService(check: { terms: TermsOfService }): boolean {
    return !!check.terms?.termsOfService;
  }

  userAgreementIsAccepted(check: { userAgreement: UserAgreementTermsOfService }): boolean {
    return check.userAgreement?.accepted;
  }

  isNewTermsOfService(check: { userAgreement: UserAgreementTermsOfService; terms: TermsOfService }): boolean {
    return check.userAgreement && check.userAgreement.acceptedAt < check.terms.createdOn;
  }

  acceptTermsOfService$(): Observable<boolean> {
    return combineLatest([this.partnerId$, this.userId$, this.platformLocationToSKey$]).pipe(
      switchMap(([partnerId, userId, platformLocationToSKey]) =>
        this.partnerUserAgreementTermsOfApiService.setUserAgreementTermsOfService({
          userAgreementTermsOfService: {
            partnerId,
            userId,
            accepted: true,
            acceptedAt: new Date(),
            feature: platformLocationToSKey.feature,
          },
        }),
      ),
      catchError((err) => {
        console.warn('setUserAgreementTermsOfService warn', err);
        return of(false);
      }),
      map(() => {
        this.snackBar.open('Terms of Service was accepted', 'OK', {
          duration: 2000,
        });
        return true;
      }),
    );
  }
}
